import React, { PureComponent } from 'react'
import classNames from 'classnames'
import {
  string, func, shape, instanceOf, bool,
} from 'prop-types'
import Button from '@active/react-ui/Button'
import { FormConsumer } from '@active/react-ui/Form'
import FormGroup from '@active/react-ui/FormGroup'
import FormLabel from '@active/react-ui/FormLabel'
import FormField from '@active/react-ui/FormField'
import Input from '@active/react-ui/Input'
import Grid from '@active/react-ui/Grid'
import GridItem from '@active/react-ui/GridItem'
import { withI18n } from '@active/react-utils/i18n'

import Form from '../../../common/InjectedForm'
import DobSelectorWithValueParse from '../../../common/DobSelectorWithValueParse'
import ConfirmedPassword from '../../../common/ConfirmedPassword'
import CountryDropdown from '../../../common/CountryDropdown'
import ActiveConsents from '../../../common/AgreementGroups'
import { noop } from '../../../../shared/utils/commonUtils'
import { emptyValidatorBuilder, nameValidatorBuilder } from '../../../../shared/validator'
import Captcha from '../../../common/Captcha/Captcha'
import countriesConfig from '../../../common/CountryDropdown/config/countriesConfig'

import './CreateAccountCommon.scss'
import I18nMessage from '../../../common/I18nMessage'
import CheckEmail from '../../../common/CheckEmail/CheckEmail'

export const CreateAccountFormPropTypes = {
  emailField: shape({
    hidden: bool, value: string, changeAble: bool, editAble: bool,
  }),
  firstNameField: shape({
    hidden: bool, value: string, optional: bool, customerValidator: func,
  }),
  lastNameField: shape({
    hidden: bool, value: string, optional: bool, customerValidator: func,
  }),
  dobField: shape({
    hidden: bool, value: instanceOf(Date), optional: bool, customerValidator: func,
  }),
  countryField: shape({
    hidden: bool, value: string, optional: bool, customerValidator: func,
  }),
  passwordField: shape({
    hidden: bool, optional: bool, disable: bool, // Once disable, The element will not be rendered
  }),
}

export const CreateAccountFormDefaultTypes = {
  emailField: {},
  firstNameField: {},
  lastNameField: {},
  dobField: {},
  countryField: {},
  passwordField: {},
}

function isValidCountryCode(countryCode) {
  return countriesConfig.filter(country => country.code === countryCode).length > 0
}

function getValidator(field, defaultValidator) {
  return (field.customerValidator ? field.customerValidator : defaultValidator)
}

function hasFieldValue(ignore, value) {
  return !ignore && value
}

const fields = ['email', 'firstName', 'lastName', 'dob', 'countryCode', 'password', 'confirmPassword', 'agreed', 'verCodeReady']
class CreateAccountForm extends PureComponent {
  static propTypes = {
    i18n: func.isRequired,

    hideCollectingConsents: bool,
    hasSubmitButton: bool,

    ...CreateAccountFormPropTypes,

    handleCreationSubmit: func.isRequired,
    handleErrorMessage: func.isRequired,
    handleExistAccount: func,
    handleCheckAccount: func,
    handldeValidationFailedCallback: func,
    submitFormTriggerBuilder: func,

    countryCodes: string,

    services: shape({
      createAccount: func.isRequired,
      key: string, // Should not have this field when we it on widget
    }).isRequired,
    configureTrack: func.isRequired,
    clientConfig: shape({
      suppressActiveWaiver: bool,
      useCustomizeWaiver: bool,
      waiverAttachementObjectIds: string,
      recaptchaUrl: string,
      recaptchaSiteKey: string,
    }).isRequired,
    passportAPIServerHost: string.isRequired,
    isPage: bool,
    showIfError: bool, // Show input element if the value is invalid
    ignoreCaptcha: bool,

    submitBtnLabel: string,
    existingAccount: bool,
    invalidEmail: bool,
  }

  static defaultProps = {
    hideCollectingConsents: true,
    hasSubmitButton: false,
    countryCodes: undefined,

    ...CreateAccountFormDefaultTypes,

    isPage: false,
    showIfError: false,
    ignoreCaptcha: false,

    handleExistAccount: noop,
    handleCheckAccount: undefined,
    handldeValidationFailedCallback: noop,
    submitFormTriggerBuilder: noop,

    submitBtnLabel: 'passport.common.btn.signUp',
    existingAccount: false,
    invalidEmail: false,
  }

  formRef

  getEmailChangeAble

  checkEmail

  constructor(props) {
    super(props)

    const initCountryCode = isValidCountryCode(props.countryField.value) ? props.countryField.value : null

    const initialValues = {}
    if (props.emailField.value) initialValues.email = props.emailField.value
    if (props.firstNameField.value) initialValues.firstName = props.firstNameField.value
    if (props.lastNameField.value) initialValues.lastName = props.lastNameField.value
    if (props.dobField.value) initialValues.dob = props.dobField.value.toISOString()
    if (initCountryCode) initialValues.countryCode = initCountryCode

    const startFromDetailData = {
      checkedResult: { newAccount: props.emailField.value },
      hasError: false,
    }
    if (props.existingAccount) {
        startFromDetailData.checkedResult = { existAccount: props.emailField.value }
        startFromDetailData.hasError = true
    } else if (props.invalidEmail) {
        startFromDetailData.hasError = true
    }

    this.state = {
      resetAgrees: false,
      selectedCountryCode: initCountryCode,
      initialValues,
      options: {
        firstNameOpt: { ...props.firstNameField },
        lastNameOpt: { ...props.lastNameField },
        dobOpt: { ...props.dobField },
        countryOpt: { ...props.countryField },
        passwordOpt: { ...props.passwordField },
      },
      signedWaiverIds: [],
      currentEmail: props.emailField.value, // Use for password validator
      startFromDetailData: startFromDetailData,
    }
  }

  componentDidMount() {
    const { configureTrack, submitFormTriggerBuilder, invalidEmail } = this.props
    configureTrack({
      eventName: 'CreateAccountForm',
      widget: 'create_account_form',
      pageType: 'create account form',
    })
    submitFormTriggerBuilder(this.handleCreateAccount, this.setFormFieldError)
    if (invalidEmail) {
      this.formRef.setFieldError('email', 'passport.error.validation.invalidEmail')
      this.formRef.setFieldTouched('email', true, false)
    }
  }

  getChangeAbleTrigger = getEmailChangeAble => {
    this.getEmailChangeAble = getEmailChangeAble
  }

  checkEmailTrigger = checkEmail => {
    this.checkEmail = checkEmail
  }

  handleCreateAccount = (params, partialUpdate) => {
    if (params) {
      const injectData = { ...params }
      if (Object.prototype.hasOwnProperty.call(injectData, 'countryCode')) {
        injectData.countryCode = isValidCountryCode(injectData.countryCode) ? injectData.countryCode : null
      }
      if (Object.prototype.hasOwnProperty.call(injectData, 'dob') && typeof injectData.dob === 'object') {
        injectData.dob = injectData.dob.toISOString()
      }
      if (Object.prototype.hasOwnProperty.call(injectData, 'password')) {
        injectData.confirmPassword = injectData.password
      }
      const updatedValues = partialUpdate ? { ...this.formRef.values, ...injectData } : { ...injectData }

      fields.forEach(f => {
        this.formRef.setFieldError(f, null)
        this.formRef.setFieldTouched(f, false, false)
      })
      Promise.all(Object.keys(updatedValues).map(async k => {
        await this.formRef.setFieldValue(k, updatedValues[k], false)
      })).then(() => {
        this.triggerCreateAccount()
      })
    } else {
      this.triggerCreateAccount()
    }
  }

  validateCreateAccountForm = async () => {
    const errors = await this.formRef.validateForm({ shouldTouchFields: true })
    return errors
  }

  triggerCreateAccount = async () => {
    const errors = await this.validateCreateAccountForm()
    if (!errors || !Object.keys(errors).length) {
      this.formRef.submitForm()
    } else {
      this.handldeValidationFailed(errors)
    }
  }

  handleChange = ({ values }) => {
    const { handleErrorMessage } = this.props
    if (this.state.cachedValue !== values) {
      this.setState({ cachedValue: values })
      handleErrorMessage(null)
    }
  }

  hasFieldsValue = () => {
    const { emailField } = this.props
    const {
      options: {
        firstNameOpt, lastNameOpt, dobOpt, countryOpt, passwordOpt,
      },
      signedWaiverIds: { length },
    } = this.state

    const readonly = !this.getEmailChangeAble()

    return (
      (!passwordOpt.disable && hasFieldValue(passwordOpt.hidden, this.formRef.values.password))
      || hasFieldValue(emailField.hidden || readonly, this.formRef.values.email)
      || hasFieldValue(firstNameOpt.hidden, this.formRef.values.firstName)
      || hasFieldValue(lastNameOpt.hidden, this.formRef.values.lastName)
      // For IE 11
      // eslint-disable-next-line no-restricted-globals
      || hasFieldValue(dobOpt.hidden, this.formRef.values.dob)
      || hasFieldValue(countryOpt.hidden, this.formRef.values.countryCode)
      || hasFieldValue(false, length)
    )
  }

  handldeValidationFailed = errors => {
    const { i18n, showIfError, handldeValidationFailedCallback } = this.props
    const {
      options: {
        firstNameOpt, lastNameOpt, dobOpt, countryOpt, passwordOpt,
      },
    } = this.state

    const messages = {}
    Object.keys(errors).forEach(k => {
      if (typeof errors[k] === 'string') {
        messages[k] = i18n(errors[k])
      } else {
        messages[k] = {}
        Object.keys(errors[k]).forEach(sk => {
          messages[k][sk] = i18n(errors[k][sk])
        })
      }
    })
    const hasValue = this.hasFieldsValue()
    handldeValidationFailedCallback({
      status: hasValue ? 'ERROR' : 'EMPTY', errors, messages,
    })
    // Confirmed with CUI Annie if has no Value, the default first and last name will not be used in the next steps.
    // So we just show error here after 'EMPTY' behavior.
    if (showIfError) {
      if ((errors.firstName && firstNameOpt.hidden) || (errors.lastName && lastNameOpt.hidden)) {
        firstNameOpt.hidden = false
        lastNameOpt.hidden = false
      }
      if (errors.dob && dobOpt.hidden) {
        dobOpt.hidden = false
      }
      if (errors.countryCode && countryOpt.hidden) {
        countryOpt.hidden = false
      }
      if (!passwordOpt.disable && errors.password && passwordOpt.hidden) {
        passwordOpt.hidden = false
      }
      this.setState({
        options: {
          firstNameOpt,
          lastNameOpt,
          dobOpt,
          countryOpt,
          passwordOpt,
        },
      })
    }
  }

  handleCheckboxChange = signedWaiverIds => {
    this.state.signedWaiverIds = [...signedWaiverIds]
    this.setState({ resetAgrees: false })
  }

  handleChangeCountry = value => {
    const { selectedCountryCode } = this.state
    if (selectedCountryCode !== value) {
      this.setState({
        selectedCountryCode: value,
        resetAgrees: true,
      })
    }
  }

  handleEmailChange = email => {
    this.setState({ currentEmail: email })
  }

  validateEmail = email => new Promise(resolve => {
    if (this.getEmailChangeAble()) {
      this.checkEmail(
        email,
        ({ existAccount }) => { existAccount ? resolve('passport.error.server.existAccount') : resolve() },
        message => resolve(message),
      )
    } else {
      resolve()
    }
  })

  handleEmailBlur = async ({ target: { value } }) => {
    if (!this.formRef.errors || !this.formRef.errors.email) {
      await this.validateEmail(value)
    }
  }

  handleSubmit = async values => {
    const {
      handleCreationSubmit, clientConfig: { recaptchaSiteKey }, isPage, ignoreCaptcha,
    } = this.props
    const waiverError = this.Sub && this.Sub.agreementValidator()
    const emailError = await this.validateEmail(values.email)

    if (!waiverError && !emailError) {
      if (isPage && !ignoreCaptcha) {
        window.grecaptcha.enterprise.ready(() => {
          window.grecaptcha.enterprise.execute(recaptchaSiteKey, { action: 'submit' }).then(token => {
            const _values = { ...values, verCode: token }
            handleCreationSubmit(_values)
          })
        })
      } else {
        handleCreationSubmit(values)
      }
      return
    }
    const errors = {}
    if (emailError) {
      errors.email = emailError
    }
    if (waiverError) {
      errors.agreed = waiverError
    }
    this.handldeValidationFailed(errors)
  }

  setFormFieldError = (fieldName, error) => {
    this.formRef.setFieldError(fieldName, error)
  }

  render() {
    const {
      // config
      hideCollectingConsents, hasSubmitButton, countryCodes,
      // Fields
      emailField,
      // lib
      clientConfig, services, passportAPIServerHost, isPage, ignoreCaptcha,
      // func
      handleExistAccount, handleCheckAccount,
      // Other
      submitBtnLabel,
      i18n,
      // initial with email
      existingAccount, invalidEmail,
    } = this.props
    const {
      selectedCountryCode, resetAgrees, initialValues, currentEmail,
      options: {
        firstNameOpt, lastNameOpt, dobOpt, countryOpt, passwordOpt,
      },
      startFromDetailData: {
        checkedResult, hasError,
      }
    } = this.state
    const { recaptchaUrl, recaptchaSiteKey, waiverInfos } = clientConfig || {}

    return (
      <Form
        initialValues={initialValues}
        onSubmit={this.handleSubmit}
        onChange={this.handleChange}
        className="passport-ui-app__create-account-form"
      >
        <Grid colGap={0}>
          <GridItem>
            {i18n('passport.common.asterisk.significance')}
            <span
              className="asterisk"
              title="Required"
            >
              *
            </span>
          </GridItem>
        </Grid>
        <Grid colGap={0} className={classNames({ 'u-hidden': emailField.hidden })}>
          <GridItem>
            <FormGroup name="email" required withoutLayout className="check-email-form-group">
              <FormLabel htmlFor="email">
                <I18nMessage id="passport.common.label.emailAddress" />
              </FormLabel>
              <FormField
                as={CheckEmail}
                changeAble={hasError || emailField.changeAble}
                existingAccount={existingAccount}
                editAble={emailField.editAble}
                checkedResult={checkedResult}
                changeExistAccount={handleExistAccount}
                services={services}
                handleEmailChange={this.handleEmailChange}
                handleCheckAccount={handleCheckAccount}
                getChangeAbleTrigger={this.getChangeAbleTrigger}
                checkEmailTrigger={this.checkEmailTrigger}
                onBlur={this.handleEmailBlur}
                requiredError="passport.error.validation.invalidEmail"
                invalidError="passport.error.validation.invalidEmail"
              />
            </FormGroup>
          </GridItem>
        </Grid>
        <Grid
          rowGap={0}
          colGap={0}
          className={classNames({
            'u-hidden': firstNameOpt.hidden && lastNameOpt.hidden,
          })}
        >
          <GridItem
            span={6}
            className={`${classNames({
              'u-hidden': firstNameOpt.hidden,
            })} passport-first_name`}
          >
            <FormGroup name="firstName" required={!firstNameOpt.optional} withoutLayout>
              <FormLabel>
                <I18nMessage id="passport.common.label.fname" />
              </FormLabel>
              <FormField
                rules="max:127"
                validate={getValidator(
                  firstNameOpt,
                  nameValidatorBuilder(i18n, 'passport.error.validation.firstNamePrompt', firstNameOpt.optional),
                )}
                autoComplete="given-name"
                as={Input}
              />
            </FormGroup>
          </GridItem>
          <GridItem
            span={6}
            className={`${classNames({
              'u-hidden': lastNameOpt.hidden,
            })} passport-last_name`}
          >
            <FormGroup name="lastName" required={!lastNameOpt.optional} withoutLayout>
              <FormLabel>
                <I18nMessage id="passport.common.label.lname" />
              </FormLabel>
              <FormField
                rules="max:127"
                validate={getValidator(
                  lastNameOpt,
                  nameValidatorBuilder(i18n, 'passport.error.validation.lastNamePrompt', lastNameOpt.optional),
                )}
                autoComplete="family-name"
                as={Input}
              />
            </FormGroup>
          </GridItem>
        </Grid>
        <Grid colGap={0} className={classNames({ 'u-hidden': dobOpt.hidden })}>
          <GridItem>
            <FormGroup name="dob" required={!dobOpt.optional} withoutLayout>
              <FormLabel>
                <I18nMessage id="passport.common.label.dob" />
              </FormLabel>
              <FormField
                rules="max:127"
                validate={getValidator(
                  dobOpt,
                  !dobOpt.optional ? emptyValidatorBuilder('passport.error.validation.dobPrompt') : undefined,
                )}
                className="passport__dob"
                as={DobSelectorWithValueParse}
              />
            </FormGroup>
          </GridItem>
        </Grid>
        <Grid colGap={0} className={classNames({ 'u-hidden': countryOpt.hidden })}>
          <GridItem>
            <FormGroup name="countryCode" required={!countryOpt.optional} withoutLayout>
              <FormLabel>
                <I18nMessage id="addressForm.addressField.country" />
              </FormLabel>
              <FormField
                rules="max:127"
                onChange={this.handleChangeCountry}
                validate={getValidator(
                  countryOpt,
                  !countryOpt.optional ? emptyValidatorBuilder('passport.error.validation.countryPrompt') : undefined,
                )}
                maxHeight="150px"
                className="passport__dob"
                as={CountryDropdown}
              />
            </FormGroup>
          </GridItem>
        </Grid>
        {!passwordOpt.disable && (
          <Grid colGap={0} className={classNames({ 'u-hidden': passwordOpt.hidden })}>
            <GridItem>
              <ConfirmedPassword
                name="password"
                required={!passwordOpt.optional}
                username={currentEmail}
                usernameSubscribe="email"
              />
            </GridItem>
          </Grid>
        )}
        {(!hideCollectingConsents && waiverInfos && waiverInfos.length) ? (
          <Grid colGap={0}>
            <GridItem rowGap={20}>
              <FormGroup name="agreed" withoutLayout required>
                <ActiveConsents
                  onRef={node => { this.Sub = node }}
                  name="agreed"
                  countryCodes={countryCodes}
                  handleCheckboxChange={this.handleCheckboxChange}
                  selectedCountryCode={selectedCountryCode}
                  resetAgrees={resetAgrees}
                  clientConfig={clientConfig}
                  passportAPIServerHost={passportAPIServerHost}
                  requiredError="passport.waiver.error.notAgreeMsg"
                />
              </FormGroup>
            </GridItem>
          </Grid>
        ) : null}
        {isPage && !ignoreCaptcha ? (
          <FormGroup name="verCodeReady" withoutLayout>
            <FormField as={Captcha} recaptchaUrl={recaptchaUrl} recaptchaSiteKey={recaptchaSiteKey} />
          </FormGroup>
        ) : null}
        {hasSubmitButton ? (
          <Grid colGap={0} rowGap={0}>
            <GridItem>
              <Button type="super" className="create-form-button" submit>
                <I18nMessage id={submitBtnLabel} />
              </Button>
            </GridItem>
          </Grid>
        ) : null}
        <FormConsumer>
          {({ form }) => {
            this.formRef = form
            return null
          }}
        </FormConsumer>
      </Form>
    )
  }
}

export default withI18n(CreateAccountForm)
